/* Petifo - cart drawer behaviour. Replaces the EGO / Opus cart drawer app.

   Almost everything visible is server-rendered Liquid (pf-cart-perks,
   cart-countdown, cart-upsell, pf-cart-foot) so Horizon's morph owns the DOM.

   THREE EXCEPTIONS, all deliberate (2026-08-30):
     1. the struck "was" price on a discounted cart line,
     2. the green per-line saving pill, and
     3. the order total printed inside the checkout button,
   because all three belong inside `snippets/cart-products.liquid` and
   `snippets/cart-summary.liquid` - theme files we agreed not to edit.

   RULES THAT KEEP THAT SAFE - do not break them:
   · paint() is IDEMPOTENT. It writes only when the value actually differs, so a
     second run causes no mutations and the observer below cannot loop.
   · every injected node carries `data-skip-node-update` so Horizon's morph
     leaves it alone instead of fighting over it.
   · no arithmetic here. The numbers are computed in Liquid by
     `snippets/pf-cart-data.liquid` and arrive pre-formatted - the shop sells in
     SEK, EUR, DKK and PLN, and parsing formatted money in four locales is how
     you show a customer a wrong number. */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__pfCartReady) return;
  window.__pfCartReady = true;

  var KEY = 'pf_cart_reserve_expiry';

  function pad(n) { return String(n).padStart(2, '0'); }

  /* ---------- reservation timer ----------
     Runs for the drawer AND the cart page, so both show the same countdown. */
  setInterval(function () {
    var all = document.querySelectorAll('.pf-countdown');
    if (!all.length) return;
    var mins = parseInt(all[0].getAttribute('data-pf-minutes'), 10) || 15;
    var expiry = parseInt(localStorage.getItem(KEY) || '0', 10);
    if (!expiry || expiry < Date.now()) {
      expiry = Date.now() + mins * 60000;
      try { localStorage.setItem(KEY, String(expiry)); } catch (e) {}
    }
    var left = Math.max(0, expiry - Date.now());
    var text = pad(Math.floor(left / 60000)) + ':' + pad(Math.floor((left % 60000) / 1000));
    all.forEach(function (cd) {
      var t = cd.querySelector('.pf-countdown__time');
      if (t && t.textContent !== text) t.textContent = text;
    });
  }, 1000);

  /* ---------- line pricing: struck was-price, red now-price, saving pill ----------

     All of it goes in the theme's OWN price cell on the right. The theme also
     renders a unit price on the left; pf-cart.css hides that one, because
     showing the price twice cost a line of height and the two numbers disagree
     as soon as quantity is above 1 (Peji 2026-08-30). */
  function paintSavings(root) {
    var data = root.querySelector('[data-pf-save-data]');
    if (!data) return;

    var rows;
    try { rows = JSON.parse(data.textContent) || []; } catch (e) { return; }

    var label = data.getAttribute('data-label') || window.PetifoI18n.cart.save;
    var byKey = {};
    rows.forEach(function (r) { if (r && r.k) byKey[r.k] = r; });

    root.querySelectorAll('[data-key]').forEach(function (row) {
      var host = row.querySelector('.cart-items__price');
      if (!host) return;

      var rec = byKey[row.getAttribute('data-key')];
      var was = host.querySelector('.pf-line-was');
      var pill = host.querySelector('.pf-save');

      if (!rec) {
        if (was) was.remove();
        if (pill) pill.remove();
        host.classList.remove('pf-is-off');
        return;
      }

      if (rec.w) {
        if (!was) {
          was = document.createElement('s');
          was.className = 'pf-line-was';
          was.setAttribute('data-skip-node-update', '');
          host.insertBefore(was, host.firstChild);
        }
        if (was.textContent !== rec.w) was.textContent = rec.w;
        host.classList.add('pf-is-off');
      } else {
        if (was) was.remove();
        host.classList.remove('pf-is-off');
      }

      var text = label + ' ' + rec.s;
      if (!pill) {
        pill = document.createElement('span');
        pill.className = 'pf-save';
        pill.setAttribute('data-skip-node-update', '');
        host.appendChild(pill);
      }
      if (pill.textContent !== text) pill.textContent = text;
    });
  }

  /* ---------- order total inside the checkout button ---------- */
  function paintTotal(root) {
    var src = root.querySelector('[data-cart-subtotal]');
    if (!src) return;
    var total = (src.textContent || '').trim();
    if (!total) return;

    root.querySelectorAll('.cart__checkout-button').forEach(function (btn) {
      var out = btn.querySelector('.pf-co__total');
      if (!out) {
        var sep = document.createElement('span');
        sep.className = 'pf-co__sep';
        sep.setAttribute('data-skip-node-update', '');
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '·';

        out = document.createElement('span');
        out.className = 'pf-co__total';
        out.setAttribute('data-skip-node-update', '');

        btn.appendChild(sep);
        btn.appendChild(out);
      }
      if (out.textContent !== total) out.textContent = total;
    });
  }

  /* ---------- paint loop ----------
     A MutationObserver is the only thing that reliably catches every morph,
     every quantity change and every Section Rendering swap. It is safe here
     precisely because paint() is idempotent: the second pass writes nothing,
     produces no mutations, and the loop dies out. takeRecords() additionally
     discards the records our own writes just generated. */
  var timer = null;
  var observer = null;

  function paint() {
    timer = null;
    var roots = document.querySelectorAll('cart-drawer-component, .cart-page');
    roots.forEach(function (root) {
      try { paintSavings(root); paintTotal(root); } catch (e) {}
    });
    if (observer) observer.takeRecords();
  }

  function schedule() {
    if (timer) return;
    timer = setTimeout(paint, 60);
  }

  function start() {
    paint();
    observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    if (observer) observer.takeRecords();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* ---------- upsell ---------- */
  document.addEventListener('change', function (e) {
    var sel = e.target.closest && e.target.closest('[data-pf-up-variant]');
    if (sel) {
      var row = sel.closest('[data-pf-up-card]');
      row.dataset.variantId = sel.value;
      var customise = row.querySelector('[data-pf-up-customise]');
      if (customise) { var url = new URL(customise.href); url.searchParams.set('variant', sel.value); customise.href = url.href; }
    }
  });

  document.addEventListener('click', function (e) {
    var add = e.target.closest && e.target.closest('[data-pf-up-add]');
    if (!add || add.disabled) return;
    var card = add.closest('[data-pf-up-card]');
    if (!card) return;
    add.disabled = true;
    addToCart(card.dataset.variantId, 1, add).then(function (ok) {
      add.disabled = false;
      if (!ok) return;
      add.classList.add('is-added');
      setTimeout(function () { add.classList.remove('is-added'); }, 1500);
    });
  });

  /* Adds through the theme's own event contract so the cart drawer, the header
     bubble and every other cart-bound section re-render exactly as they do for
     a native add. */
  async function addToCart(variantId, quantity, sourceEl) {
    var ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      ids.add(el.dataset.sectionId);
    });
    var sections = Array.from(ids).join(',');
    var deferred = CartLinesUpdateEvent.createPromise();
    (sourceEl || document.body).dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: quantity }],
      promise: deferred.promise
    }));
    try {
      var res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity }], sections: sections })
      });
      var data = await res.json();
      if (data.status) { deferred.reject(new Error(data.description || 'Add to cart failed')); return false; }
      var cart = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: data.sections,
          source: 'pf-cart-upsell',
          didError: false
        }
      });
      return true;
    } catch (err) {
      deferred.reject(err);
      return false;
    }
  }
})();
